import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useTranslation } from "react-i18next";

/**
 * Admin Pricing Page component for managing product pricing
 * @returns {JSX.Element} The Admin Pricing Page component
 */
const AdminPricingPage = () => {
  const { t } = useTranslation();
  const [pricingRules, setPricingRules] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentRule, setCurrentRule] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Mock data for demonstration
  useEffect(() => {
    // This would typically be an API call
    setPricingRules([
      {
        id: 1,
        name: "Volume Discount - Small",
        type: "volume",
        customerGroup: "all",
        minQuantity: 10,
        maxQuantity: 49,
        discountPercentage: 5,
        active: true,
      },
      {
        id: 2,
        name: "Volume Discount - Medium",
        type: "volume",
        customerGroup: "all",
        minQuantity: 50,
        maxQuantity: 99,
        discountPercentage: 10,
        active: true,
      },
      {
        id: 3,
        name: "Premium Customer Discount",
        type: "customer",
        customerGroup: "premium",
        discountPercentage: 15,
        active: true,
      },
    ]);
  }, []);

  const handleOpenDialog = (rule = null) => {
    setCurrentRule(
      rule || {
        name: "",
        type: "volume",
        customerGroup: "all",
        minQuantity: 0,
        maxQuantity: 0,
        discountPercentage: 0,
        active: true,
      }
    );
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentRule(null);
  };

  const handleSaveRule = () => {
    if (currentRule.id) {
      // Update existing rule
      setPricingRules(
        pricingRules.map((rule) =>
          rule.id === currentRule.id ? currentRule : rule
        )
      );
      setSnackbar({
        open: true,
        message: t("Pricing rule updated successfully"),
        severity: "success",
      });
    } else {
      // Add new rule
      setPricingRules([...pricingRules, { ...currentRule, id: Date.now() }]);
      setSnackbar({
        open: true,
        message: t("Pricing rule added successfully"),
        severity: "success",
      });
    }
    handleCloseDialog();
  };

  const handleDeleteRule = (id) => {
    setPricingRules(pricingRules.filter((rule) => rule.id !== id));
    setSnackbar({
      open: true,
      message: t("Pricing rule deleted successfully"),
      severity: "success",
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentRule({ ...currentRule, [name]: value });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          {t("Pricing Management")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t("Add Pricing Rule")}
        </Button>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 640 }}>
          <Table stickyHeader aria-label="pricing rules table">
            <TableHead>
              <TableRow>
                <TableCell>{t("Name")}</TableCell>
                <TableCell>{t("Type")}</TableCell>
                <TableCell>{t("Customer Group")}</TableCell>
                <TableCell>{t("Min Quantity")}</TableCell>
                <TableCell>{t("Max Quantity")}</TableCell>
                <TableCell>{t("Discount %")}</TableCell>
                <TableCell>{t("Status")}</TableCell>
                <TableCell>{t("Actions")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pricingRules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>{rule.name}</TableCell>
                  <TableCell>
                    {rule.type === "volume"
                      ? t("Volume Discount")
                      : t("Customer Group Discount")}
                  </TableCell>
                  <TableCell>
                    {rule.customerGroup === "all"
                      ? t("All Customers")
                      : rule.customerGroup === "premium"
                      ? t("Premium Customers")
                      : rule.customerGroup}
                  </TableCell>
                  <TableCell>
                    {rule.type === "volume" ? rule.minQuantity : "-"}
                  </TableCell>
                  <TableCell>
                    {rule.type === "volume" ? rule.maxQuantity : "-"}
                  </TableCell>
                  <TableCell>{rule.discountPercentage}%</TableCell>
                  <TableCell>
                    {rule.active ? t("Active") : t("Inactive")}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(rule)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteRule(rule.id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Dialog for adding/editing pricing rules */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md">
        <DialogTitle>
          {currentRule && currentRule.id
            ? t("Edit Pricing Rule")
            : t("Add Pricing Rule")}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label={t("Rule Name")}
                fullWidth
                value={currentRule?.name || ""}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Rule Type")}</InputLabel>
                <Select
                  name="type"
                  value={currentRule?.type || "volume"}
                  onChange={handleInputChange}
                  label={t("Rule Type")}
                >
                  <MenuItem value="volume">{t("Volume Discount")}</MenuItem>
                  <MenuItem value="customer">
                    {t("Customer Group Discount")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Customer Group")}</InputLabel>
                <Select
                  name="customerGroup"
                  value={currentRule?.customerGroup || "all"}
                  onChange={handleInputChange}
                  label={t("Customer Group")}
                >
                  <MenuItem value="all">{t("All Customers")}</MenuItem>
                  <MenuItem value="premium">{t("Premium Customers")}</MenuItem>
                  <MenuItem value="wholesale">
                    {t("Wholesale Customers")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {currentRule?.type === "volume" && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="minQuantity"
                    label={t("Minimum Quantity")}
                    type="number"
                    fullWidth
                    value={currentRule?.minQuantity || 0}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="maxQuantity"
                    label={t("Maximum Quantity")}
                    type="number"
                    fullWidth
                    value={currentRule?.maxQuantity || 0}
                    onChange={handleInputChange}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                name="discountPercentage"
                label={t("Discount Percentage")}
                type="number"
                fullWidth
                value={currentRule?.discountPercentage || 0}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: "%",
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>{t("Status")}</InputLabel>
                <Select
                  name="active"
                  value={currentRule?.active || true}
                  onChange={handleInputChange}
                  label={t("Status")}
                >
                  <MenuItem value={true}>{t("Active")}</MenuItem>
                  <MenuItem value={false}>{t("Inactive")}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t("Cancel")}</Button>
          <Button onClick={handleSaveRule} variant="contained" color="primary">
            {t("Save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPricingPage;
